import api from './client';

/**
 * Check GSC connection status
 */
export async function getGSCStatus() {
  const response = await api.get('/api/gsc/status');
  return response.data;
}

/**
 * Get OAuth authorization URL
 */
export async function getAuthUrl() {
  const response = await api.get('/api/gsc/auth-url');
  return response.data;
}

/**
 * Disconnect from GSC
 */
export async function disconnectGSC() {
  const response = await api.post('/api/gsc/disconnect');
  return response.data;
}

/**
 * List verified sites (enriched with project linking info)
 */
export async function listSites() {
  const response = await api.get('/api/gsc/sites');
  return response.data;
}

/**
 * Link a GSC site to a project
 */
export async function linkSiteToProject(siteUrl, projectId) {
  const response = await api.post('/api/gsc/link-site', { siteUrl, projectId });
  return response.data;
}

/**
 * Unlink a GSC site from a project
 */
export async function unlinkSite(projectId) {
  const response = await api.delete(`/api/gsc/link-site/${projectId}`);
  return response.data;
}

/**
 * Get linked site for a project
 */
export async function getLinkedSite(projectId) {
  const response = await api.get(`/api/gsc/linked-site/${projectId}`);
  return response.data;
}

/**
 * Get performance data for a site (pages)
 */
export async function getPerformance(siteUrl, options = {}) {
  const params = new URLSearchParams();
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);
  if (options.startRow != null) params.set('startRow', options.startRow);
  if (options.rowLimit) params.set('rowLimit', options.rowLimit);
  if (options.searchType) params.set('searchType', options.searchType);

  const response = await api.get(
    `/api/gsc/performance/${encodeURIComponent(siteUrl)}?${params}`
  );
  return response.data;
}

/**
 * Get keyword performance data
 */
export async function getKeywords(siteUrl, options = {}) {
  const params = new URLSearchParams();
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);
  if (options.startRow != null) params.set('startRow', options.startRow);
  if (options.rowLimit) params.set('rowLimit', options.rowLimit);
  if (options.pageFilter) params.set('pageFilter', options.pageFilter);
  if (options.queryFilter) params.set('queryFilter', options.queryFilter);
  if (options.device) params.set('device', options.device);
  if (options.country) params.set('country', options.country);

  const response = await api.get(
    `/api/gsc/keywords/${encodeURIComponent(siteUrl)}?${params}`
  );
  return response.data;
}

/**
 * Get page performance trend over time
 */
export async function getPageTrend(siteUrl, pageUrl, options = {}) {
  const params = new URLSearchParams();
  params.set('pageUrl', pageUrl);
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);

  const response = await api.get(
    `/api/gsc/page-trend/${encodeURIComponent(siteUrl)}?${params}`
  );
  return response.data;
}

/**
 * Inspect a single URL
 */
export async function inspectUrlApi(siteUrl, inspectionUrl) {
  const response = await api.post('/api/gsc/inspect', { siteUrl, inspectionUrl });
  return response.data;
}

/**
 * Batch inspect project's backlink target URLs
 */
export async function batchInspect(siteUrl, projectId, limit = 50) {
  const response = await api.post('/api/gsc/inspect/batch', { siteUrl, projectId, limit });
  return response.data;
}

/**
 * Get cached inspection results
 */
export async function getInspectionResults(siteUrl, projectId = null) {
  const params = new URLSearchParams();
  if (siteUrl) params.set('siteUrl', siteUrl);
  if (projectId) params.set('projectId', projectId);

  const response = await api.get(`/api/gsc/inspect/results?${params}`);
  return response.data;
}

/**
 * Get daily inspection budget
 */
export async function getInspectionBudget() {
  const response = await api.get('/api/gsc/inspect/budget');
  return response.data;
}

/**
 * List sitemaps for a site
 */
export async function getSitemaps(siteUrl) {
  const response = await api.get(
    `/api/gsc/sitemaps/${encodeURIComponent(siteUrl)}`
  );
  return response.data;
}

/**
 * Submit a sitemap
 */
export async function submitSitemap(siteUrl, feedpath) {
  const response = await api.post(
    `/api/gsc/sitemaps/${encodeURIComponent(siteUrl)}`,
    { feedpath }
  );
  return response.data;
}

/**
 * Delete a sitemap
 */
export async function deleteSitemap(siteUrl, feedpath) {
  const params = new URLSearchParams();
  params.set('feedpath', feedpath);
  const response = await api.delete(
    `/api/gsc/sitemaps/${encodeURIComponent(siteUrl)}?${params}`
  );
  return response.data;
}

/**
 * Get backlink-GSC performance correlation for a project
 */
export async function getBacklinkPerformance(projectId) {
  const response = await api.get(`/api/gsc/backlink-performance/${projectId}`);
  return response.data;
}

/**
 * Trigger manual snapshot collection
 */
export async function collectSnapshot(siteUrl) {
  const response = await api.post('/api/gsc/snapshot/collect', { siteUrl });
  return response.data;
}

/**
 * Get historical snapshot data
 */
export async function getSnapshotHistory(siteUrl, options = {}) {
  const params = new URLSearchParams();
  params.set('siteUrl', siteUrl);
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);
  if (options.dimensionType) params.set('dimensionType', options.dimensionType);

  const response = await api.get(`/api/gsc/snapshot/history?${params}`);
  return response.data;
}

/**
 * Get snapshot date coverage
 */
export async function getSnapshotCoverage(siteUrl) {
  const params = new URLSearchParams();
  params.set('siteUrl', siteUrl);

  const response = await api.get(`/api/gsc/snapshot/coverage?${params}`);
  return response.data;
}

/**
 * Discover untracked backlinks
 */
export async function discoverUntracked(siteUrl, projectId = null) {
  const params = projectId ? `?projectId=${projectId}` : '';
  const response = await api.get(
    `/api/gsc/discover/${encodeURIComponent(siteUrl)}${params}`
  );
  return response.data;
}

/**
 * Bulk import discovered links into a project
 */
export async function importDiscovered(projectId, pages) {
  const response = await api.post('/api/gsc/import-discovered', { projectId, pages });
  return response.data;
}

/**
 * Generate disavow file
 */
export async function generateDisavow(links, projectId = null, notes = null) {
  const response = await api.post('/api/gsc/disavow/generate', { links, projectId, notes });
  return response.data;
}

/**
 * Parse disavow file content
 */
export async function parseDisavow(content) {
  const response = await api.post('/api/gsc/disavow/parse', { content });
  return response.data;
}

/**
 * Get disavow suggestions
 */
export async function getDisavowSuggestions(projectId = null, spamThreshold = 50) {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (spamThreshold) params.set('spamThreshold', spamThreshold);

  const response = await api.get(`/api/gsc/disavow/suggestions?${params}`);
  return response.data;
}

/**
 * Get disavow file history
 */
export async function getDisavowHistory(projectId = null) {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);

  const response = await api.get(`/api/gsc/disavow/history?${params}`);
  return response.data;
}
